import React, { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_CLS = { activo: 'bg-emerald-500/20 text-emerald-300', saldo_bajo: 'bg-amber-500/20 text-amber-300', servicios_pausados: 'bg-rose-500/20 text-rose-300' };

export default function AdminPortalHub({ authFetch }) {
  const [clients, setClients] = useState(null);
  const [msg, setMsg] = useState(null);
  const [charge, setCharge] = useState({ client_id: '', servicio: '', coste_base_creditos: '' });
  const [recharge, setRecharge] = useState({ client_id: '', credits: '', note: '' });
  const [nuevo, setNuevo] = useState({ name: '', company: '', email: '', whatsapp: '', credits_balance: '' });

  const load = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/admin/portal/clients`);
      if (res.ok) setClients((await res.json()).clients || []);
      else setClients([]);
    } catch { setClients([]); }
  }, [authFetch]);
  useEffect(() => { load(); }, [load]);

  const post = async (url, body, okText) => {
    setMsg(null);
    try {
      const res = await authFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json();
      if (res.ok) { setMsg({ ok: true, text: okText(data) }); load(); return true; }
      setMsg({ ok: false, text: data.detail || 'Error' });
    } catch { setMsg({ ok: false, text: 'Error de conexión' }); }
    return false;
  };

  const uploadCsv = async (file) => {
    setMsg(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await authFetch(`${API}/admin/portal/import-csv`, { method: 'POST', body: fd });
      const data = await res.json();
      if (res.ok) { setMsg({ ok: true, text: `✅ CSV importado: ${data.created} nuevos, ${data.updated} actualizados${data.errors?.length ? ` · avisos: ${data.errors.join(' | ')}` : ''}` }); load(); }
      else setMsg({ ok: false, text: data.detail || 'Error importando CSV' });
    } catch { setMsg({ ok: false, text: 'Error de conexión' }); }
  };

  if (clients === null) return <div className="flex items-center gap-2 p-8 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /> Cargando portal de clientes…</div>;

  const input = "rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-gray-500";
  const sel = (v, cb) => (
    <select value={v} onChange={cb} className={input}>
      <option value="">— Cliente —</option>
      {clients.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.credits_balance} cr</option>)}
    </select>
  );

  return (
    <div className="space-y-5" data-testid="admin-portal-hub">
      {msg && <p className={`rounded-xl px-4 py-2.5 text-sm font-semibold ${msg.ok ? 'bg-emerald-500/15 text-emerald-300' : 'bg-rose-500/15 text-rose-300'}`} data-testid="portal-admin-msg">{msg.text}</p>}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <h3 className="text-sm font-black text-white">📥 Importar cartera (CSV)</h3>
          <p className="mt-1 text-[11px] text-gray-400">Columnas: nombre, empresa, email, whatsapp, saldo_inicial, notas (separador , o ;)</p>
          <input type="file" accept=".csv" onChange={(e) => e.target.files?.[0] && uploadCsv(e.target.files[0])} className="mt-3 w-full text-xs text-gray-300 file:mr-3 file:rounded-full file:border-0 file:bg-cyan-400 file:px-4 file:py-2 file:text-xs file:font-bold file:text-[#02040a]" data-testid="portal-csv-input" />
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <h3 className="text-sm font-black text-white">💸 Registrar consumo (+20% auto)</h3>
          <div className="mt-3 space-y-2">
            {sel(charge.client_id, (e) => setCharge((p) => ({ ...p, client_id: e.target.value })))}
            <input value={charge.servicio} onChange={(e) => setCharge((p) => ({ ...p, servicio: e.target.value }))} placeholder="Servicio (ej. Landing page)" className={`${input} w-full`} data-testid="charge-service" />
            <input type="number" value={charge.coste_base_creditos} onChange={(e) => setCharge((p) => ({ ...p, coste_base_creditos: e.target.value }))} placeholder="Créditos base" className={`${input} w-full`} data-testid="charge-credits" />
            <button onClick={() => post(`${API}/admin/portal/charge`, charge, (d) => `✅ Consumo: ${d.consumption.coste_base_creditos} + ${d.consumption.gestion_20pct} gestión = ${d.consumption.total_descontado} cr · saldo ${d.consumption.saldo_restante}`)} className="w-full rounded-xl bg-amber-500 py-2 text-sm font-black text-[#02040a]" data-testid="charge-btn">Descontar</button>
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <h3 className="text-sm font-black text-white">🔋 Recargar créditos</h3>
          <div className="mt-3 space-y-2">
            {sel(recharge.client_id, (e) => setRecharge((p) => ({ ...p, client_id: e.target.value })))}
            <input type="number" value={recharge.credits} onChange={(e) => setRecharge((p) => ({ ...p, credits: e.target.value }))} placeholder="Créditos a añadir" className={`${input} w-full`} data-testid="recharge-credits" />
            <input value={recharge.note} onChange={(e) => setRecharge((p) => ({ ...p, note: e.target.value }))} placeholder="Nota (ej. pago Stripe #...)" className={`${input} w-full`} data-testid="recharge-note" />
            <button onClick={() => post(`${API}/admin/portal/recharge`, recharge, (d) => `✅ Recarga OK · nuevo saldo ${d.new_balance}${d.reactivated ? ' · servicios REANUDADOS' : ''}`)} className="w-full rounded-xl bg-emerald-500 py-2 text-sm font-black text-[#02040a]" data-testid="recharge-btn">Recargar</button>
          </div>
        </div>
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <h3 className="text-sm font-black text-white">➕ Alta manual de cliente</h3>
        <div className="mt-3 grid gap-2 sm:grid-cols-5">
          <input value={nuevo.name} onChange={(e) => setNuevo((p) => ({ ...p, name: e.target.value }))} placeholder="Nombre *" className={input} data-testid="new-client-name" />
          <input value={nuevo.company} onChange={(e) => setNuevo((p) => ({ ...p, company: e.target.value }))} placeholder="Empresa" className={input} />
          <input value={nuevo.email} onChange={(e) => setNuevo((p) => ({ ...p, email: e.target.value }))} placeholder="Email" className={input} data-testid="new-client-email" />
          <input value={nuevo.whatsapp} onChange={(e) => setNuevo((p) => ({ ...p, whatsapp: e.target.value }))} placeholder="WhatsApp" className={input} />
          <input type="number" value={nuevo.credits_balance} onChange={(e) => setNuevo((p) => ({ ...p, credits_balance: e.target.value }))} placeholder="Saldo inicial" className={input} data-testid="new-client-balance" />
        </div>
        <button onClick={async () => { if (await post(`${API}/admin/portal/clients`, nuevo, (d) => `✅ Cliente ${d.client.name} creado · código de acceso: ${d.client.access_code}`)) setNuevo({ name: '', company: '', email: '', whatsapp: '', credits_balance: '' }); }} className="mt-2 rounded-xl bg-cyan-400 px-5 py-2 text-sm font-black text-[#02040a]" data-testid="new-client-btn">Crear cliente</button>
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <h3 className="text-sm font-black text-white">👥 Clientes del portal ({clients.length})</h3>
        <div className="mt-3 max-h-96 space-y-1.5 overflow-y-auto" data-testid="portal-clients-list">
          {clients.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2" data-testid={`portal-client-${c.id}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-white">{c.name} {c.company ? `· ${c.company}` : ''}</p>
                <p className="text-[11px] text-gray-400">{c.email || 'sin email'} · Acceso: <b className="text-cyan-300">{c.access_code}</b> · entra en /portal</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-black ${STATUS_CLS[c.status] || 'bg-white/10 text-gray-300'}`}>{c.status}</span>
                <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-black text-white">{c.credits_balance} cr</span>
              </div>
            </div>
          ))}
          {!clients.length && <p className="rounded-xl bg-white/[0.02] p-4 text-center text-sm text-gray-500">Sin clientes. Importa tu CSV o crea uno manual.</p>}
        </div>
      </div>
    </div>
  );
}
